<template>
  <teleport to="body">
    <transition name="help-fade">
      <div v-if="state.open" class="help-backdrop" @click="close"></div>
    </transition>
    <transition name="help-slide">
      <aside v-if="state.open" class="help-drawer" role="dialog" :aria-label="$t('help.title')">
        <!-- actions en tête du panneau : Premiers pas (gérant) et support -->
        <div class="help-actions">
          <!-- gérant : « Premiers pas » (mise en route de la boutique) -->
          <button v-if="$can('category_create')" type="button" class="btn btn-sm btn-light flex-grow-1" @click="openSetup">
            <i class="bi bi-signpost-split me-1"></i>{{ $t('onboarding.title') }}
          </button>
          <router-link :to="{ name: 'my-tickets-path' }" class="btn btn-sm btn-light-primary flex-grow-1" @click="close">
            <i class="bi bi-headset me-1"></i>{{ $t('help.contact_support') }}
          </router-link>
          <!-- fermeture : coin en haut à droite -->
          <button type="button" class="btn btn-sm btn-icon btn-light flex-shrink-0" :title="$t('button.close')" @click="close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <header class="help-header">
          <button v-if="guide" type="button" class="btn btn-sm btn-icon btn-light me-2" :title="$t('help.back')" @click="state.guideId = null">
            <i class="bi bi-arrow-left"></i>
          </button>
          <h3 class="help-title">{{ guide ? guide.title : $t('help.title') }}</h3>
        </header>

        <!-- Un guide : ses étapes -->
        <div v-if="guide" class="help-body">
          <div class="help-section-chip"><i :class="sectionOf(guide).icon"></i> {{ sectionOf(guide).label }}</div>
          <ol class="help-steps">
            <li v-for="(step, index) in guide.steps" :key="index" v-html="step"></li>
          </ol>
          <div v-if="guide.tip" class="help-tip">
            <i class="bi bi-lightbulb"></i>
            <span>{{ guide.tip }}</span>
          </div>
          <div v-if="related.length" class="mt-8">
            <div class="help-group-title">{{ $t('help.related') }}</div>
            <button v-for="item in related" :key="item.id" type="button" class="help-link" @click="state.guideId = item.id">
              <span>{{ item.title }}</span><i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- Liste : recherche, guides de la page, tous les guides -->
        <div v-else class="help-body">
          <div class="position-relative mb-6">
            <i class="bi bi-search help-search-icon"></i>
            <input ref="search" v-model="query" type="search" class="form-control form-control-solid ps-10" :placeholder="$t('help.search')" />
          </div>

          <template v-if="query.trim()">
            <div class="help-group-title">{{ $t('help.results') }}</div>
            <button v-for="item in results" :key="item.id" type="button" class="help-link" @click="state.guideId = item.id">
              <span>{{ item.title }}<small class="d-block text-muted">{{ sectionOf(item).label }}</small></span><i class="bi bi-chevron-right"></i>
            </button>
            <p v-if="!results.length" class="text-muted">{{ $t('help.no_result') }}</p>
          </template>

          <template v-else>
            <template v-if="onThisPage.length">
              <div class="help-group-title">{{ $t('help.on_this_page') }}</div>
              <button v-for="item in onThisPage" :key="item.id" type="button" class="help-link help-link--page" @click="state.guideId = item.id">
                <span>{{ item.title }}</span><i class="bi bi-chevron-right"></i>
              </button>
            </template>

            <div class="help-group-title mt-6">{{ $t('help.all_guides') }}</div>
            <div v-for="section in sections" :key="section.key" class="help-section">
              <button type="button" class="help-section-toggle" @click="toggleSection(section.key)">
                <i :class="section.icon" class="me-3"></i>
                <span class="flex-grow-1 text-start">{{ section.label }}</span>
                <span class="text-muted fs-8 me-2">{{ section.guides.length }}</span>
                <i class="bi" :class="openSections.includes(section.key) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <div v-if="openSections.includes(section.key)" class="ps-4">
                <button v-for="item in section.guides" :key="item.id" type="button" class="help-link" @click="state.guideId = item.id">
                  <span>{{ item.title }}</span><i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </template>
        </div>

      </aside>
    </transition>
  </teleport>
</template>

<script>
/**
 * Aide contextuelle : panneau à droite avec les guides « Comment faire… ». Il propose d'abord
 * ceux de la page en cours, puis tous les guides par thème, avec une recherche. Les guides
 * suivent les droits et les modules de l'utilisateur (src/help/guides.js).
 */
import { helpState, closeHelp } from '@/help/helpState'
import { HELP_GUIDES, HELP_SECTIONS } from '@/help/guides'
import { HELP_GUIDES_EN, HELP_SECTIONS_EN } from '@/help/guides.en'
import { openOnboarding } from '@/onboarding/onboardingState'

const strip = (html) => html.replace(/<[^>]+>/g, '')
const normalize = (text) => text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export default {
  name: 'HelpDrawer',
  data() {
    return {
      state: helpState,
      query: '',
      openSections: [],
    }
  },
  computed: {
    isEnglish() {
      return String(this.$i18n.locale || '').startsWith('en')
    },
    // guides dans la langue de l'interface (textes anglais par id, le reste reste celui de guides.js)
    guides() {
      return HELP_GUIDES
        .filter((guide) => this.visible(guide))
        .map((guide) => (this.isEnglish && HELP_GUIDES_EN[guide.id] ? { ...guide, ...HELP_GUIDES_EN[guide.id] } : guide))
    },
    localizedSections() {
      return HELP_SECTIONS.map((section) => (this.isEnglish ? { ...section, label: HELP_SECTIONS_EN[section.key] || section.label } : section))
    },
    guide() {
      return this.guides.find((guide) => guide.id === this.state.guideId) || null
    },
    routeNames() {
      return this.$route.matched.map((record) => record.name).filter(Boolean)
    },
    onThisPage() {
      return this.guides.filter((guide) => (guide.routes || []).some((name) => this.routeNames.includes(name)))
    },
    sections() {
      return this.localizedSections
        .map((section) => ({ ...section, guides: this.guides.filter((guide) => guide.section === section.key) }))
        .filter((section) => section.guides.length)
    },
    results() {
      const words = normalize(this.query).split(/\s+/).filter(Boolean)
      return this.guides.filter((guide) => {
        const text = normalize([guide.title, ...guide.steps, guide.tip || ''].map(strip).join(' '))
        return words.every((word) => text.includes(word))
      })
    },
    related() {
      return this.guide ? this.guides.filter((item) => item.section === this.guide.section && item.id !== this.guide.id).slice(0, 4) : []
    },
  },
  watch: {
    'state.open'(open) {
      if (!open) return
      this.query = ''
      // le thème de la page en cours est déplié
      const current = this.onThisPage[0]
      this.openSections = current ? [current.section] : []
      this.$nextTick(() => this.$refs.search?.focus())
    },
    $route() {
      if (this.state.open) this.state.guideId = null
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    visible(guide) {
      if (guide.module && !this.$hasModule(guide.module)) return false
      if (guide.permission && !this.$can(guide.permission)) return false
      return true
    },
    sectionOf(guide) {
      return this.localizedSections.find((section) => section.key === guide.section) || {}
    },
    toggleSection(key) {
      this.openSections = this.openSections.includes(key)
        ? this.openSections.filter((item) => item !== key)
        : [...this.openSections, key]
    },
    close() {
      closeHelp()
    },
    openSetup() {
      closeHelp()
      openOnboarding()
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.state.open) closeHelp()
    },
  },
}
</script>

<style scoped>
.help-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1060;
}
.help-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 100%;
  background: #fff;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.12);
  z-index: 1061;
  display: flex;
  flex-direction: column;
}
.help-header {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #eff2f5;
}
.help-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #181c32;
}
.help-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}
.help-actions {
  display: flex;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid #eff2f5;
}
.help-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a5b7;
}
.help-group-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a1a5b7;
  margin-bottom: 8px;
}
.help-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  color: #181c32;
  font-weight: 500;
}
.help-link:hover {
  background: #f5f8fa;
  color: var(--kt-primary);
}
.help-link i {
  font-size: 0.75rem;
  color: #b5b5c3;
}
.help-link--page {
  background: var(--kt-primary-light, #f1f4fb);
  margin-bottom: 6px;
}
.help-section-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px dashed #eff2f5;
  background: transparent;
  font-weight: 600;
  color: #3f4254;
}
.help-section-toggle > i:first-child {
  color: var(--kt-primary);
}
.help-section-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--kt-primary);
  background: var(--kt-primary-light, #f1f4fb);
  border-radius: 999px;
  padding: 4px 12px;
  margin-bottom: 16px;
}
.help-steps {
  counter-reset: step;
  list-style: none;
  padding: 0;
  margin: 0;
}
.help-steps li {
  counter-increment: step;
  position: relative;
  padding: 0 0 18px 42px;
  line-height: 1.55;
  color: #3f4254;
}
.help-steps li::before {
  content: counter(step);
  position: absolute;
  left: 0;
  top: -2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--kt-primary);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.help-steps li:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 13px;
  top: 30px;
  bottom: 4px;
  width: 2px;
  background: #eff2f5;
}
.help-tip {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff8dd;
  color: #5e6278;
  font-size: 0.9rem;
}
.help-tip i {
  color: #f6a609;
  font-size: 1.1rem;
}
.help-slide-enter-active,
.help-slide-leave-active {
  transition: transform 0.2s ease;
}
.help-slide-enter-from,
.help-slide-leave-to {
  transform: translateX(100%);
}
.help-fade-enter-active,
.help-fade-leave-active {
  transition: opacity 0.2s ease;
}
.help-fade-enter-from,
.help-fade-leave-to {
  opacity: 0;
}
</style>
